import {Helmet} from '@dr.pogodin/react-helmet';
import {Disclaimer, DataColumn, SlideImage} from '@online-offer/shared';
import {
  Box,
  Center,
  Grid,
  GridItem,
  Heading,
  Hide,
  Show,
  Space,
  Stack,
  Text,
  VStack,
  type Integer,
  type ValueByDevice,
} from 'platform/foundation';

import {splitAt} from 'ramda';

import {OnlineOfferAlternative} from '../../api/types/onlineOfferAlternativesTypes';
import {AlternativeChoiceCard} from '../../components/AlternativeChoiceCard/AlternativeChoiceCard';
import {CardContainer} from '../../components/CardContainer/CardContainer';
import {IllustrativePhotoCaption} from '../../components/IllustrativePhotoCaption/IllustrativePhotoCaption';
import {PhotoCarousel} from '../../components/PhotoCarousel/PhotoCarousel';
import {Separator} from '../../components/Separator/Separator';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {buildComparisonRows} from '../../utils/buildComparisonRows';
import {getAlternativeLabel} from '../../utils/getAlternativeLabel';
import {getComparisonRowLabel} from '../../utils/getComparisonRowLabel';
import {translateWithFallback as t} from '../../utils/translateWithFallback';
import {useFormatComparisonValue} from '../AlternativesComparison/hooks/useFormatComparisonValue';
import {SellerCard} from '../SellerCard/SellerCard';
import {AlternativeEquipment} from './components/AlternativeEquipment';
import {AlternativePriceCard} from './components/AlternativePriceCard';

const EMPTY = '—';
// Shown in the price card instead, next to the price it limits.
const ROWS_IN_PRICE_CARD = ['validUntil'];
const CONTENT_MAX_WIDTH = 440;
const CONTENT_PADDING_HORIZONTAL: ValueByDevice<Integer> = [3, 8, 20, 20];

interface AlternativeDetailProps {
  alternative: OnlineOfferAlternative;
}

/**
 * The offer page body for an alternative that is not the deal vehicle. It is drawn from the
 * alternative's own snapshot, with the same layout as the deal vehicle: vehicle on the left,
 * price and the customer's choice on the right.
 */
export function AlternativeDetail(props: AlternativeDetailProps) {
  const {disclaimer} = useGetOnlineOfferData();
  const formatValue = useFormatComparisonValue();

  const vehicleRows = buildComparisonRows([props.alternative])
    .filter((row) => row.group === 'vehicle' && !ROWS_IN_PRICE_CARD.includes(row.id))
    .map((row) => {
      const [value] = row.values;
      const formatted = value ? formatValue(value) : EMPTY;

      return {label: getComparisonRowLabel(row), value: formatted === EMPTY ? null : formatted};
    });
  const [leftRows, rightRows] = splitAt(Math.ceil(vehicleRows.length / 2), vehicleRows);

  // Relative mock URLs must resolve too: the carousel resizes images through `new URL()`.
  const images = props.alternative.imageUrls.map((url, index): SlideImage => ({
    type: 'image',
    id: url,
    url,
    resizeUrl: new URL(url, window.location.origin).href,
    fileName: `${props.alternative.letter}-${index + 1}`,
  }));

  return (
    <>
      <Helmet>
        <title>{props.alternative.title}</title>
      </Helmet>

      <section id="hero" data-testid="alternativeDetail">
        <VStack align="center">
          {/* Same box as the body below, so the title lines up with the content it introduces. */}
          <Box
            width="100%"
            maxWidth={CONTENT_MAX_WIDTH}
            paddingTop={[6, 8, 10]}
            paddingBottom={images.length > 0 ? [4, 6, 8] : 0}
            paddingHorizontal={CONTENT_PADDING_HORIZONTAL}
          >
            <VStack spacing={2}>
              <Text size="small" alternative color="accent">
                {getAlternativeLabel(props.alternative)}
              </Text>
              <Heading size={1}>{props.alternative.title}</Heading>
              <Text size="small" color="secondary">
                {[
                  formatValue({
                    type: 'source',
                    kind: props.alternative.kind,
                    condition: props.alternative.condition,
                  }),
                  formatValue({type: 'availability', availability: props.alternative.availability}),
                ].join(' · ')}
              </Text>
            </VStack>
          </Box>
          <Show when={images.length > 0}>
            <Box width="100%">
              <PhotoCarousel
                lightboxId="alternative"
                images={images}
                isIllustrative={props.alternative.imagesIllustrative}
              />
            </Box>
          </Show>
          <Show when={props.alternative.imagesIllustrative}>
            <Box
              width="100%"
              maxWidth={CONTENT_MAX_WIDTH}
              paddingTop={2}
              paddingHorizontal={CONTENT_PADDING_HORIZONTAL}
            >
              <IllustrativePhotoCaption />
            </Box>
          </Show>
        </VStack>
      </section>

      <Space vertical={[6, 6, 10]} />

      <Center>
        <Box
          flexGrow={1}
          paddingHorizontal={CONTENT_PADDING_HORIZONTAL}
          maxWidth={CONTENT_MAX_WIDTH}
        >
          <Stack direction={['column-reverse', 'column-reverse', 'row']} spacing={8} width="100%">
            <VStack width="100%">
              <CardContainer
                heading={t('alternatives.comparison.groups.vehicle', 'Vehicle & delivery')}
              >
                <Grid columns={[1, 2]} spacing={[0, 8]}>
                  <GridItem>
                    <DataColumn data={leftRows} />
                  </GridItem>
                  <GridItem>
                    <DataColumn data={rightRows} />
                  </GridItem>
                </Grid>
              </CardContainer>
              <Space vertical={8} />
              <Show when={props.alternative.equipment.length > 0}>
                <Separator />
                <Space vertical={8} />
                <AlternativeEquipment equipment={props.alternative.equipment} />
                <Space vertical={8} />
              </Show>
              <Show onMobile onTablet>
                <SellerCard />
              </Show>
              <Disclaimer disclaimer={disclaimer} />
            </VStack>
            <VStack spacing={[6, 6, 8]} width={['100%', '100%', 90]} shrink={0}>
              <AlternativePriceCard alternative={props.alternative} />
              <AlternativeChoiceCard alternative={props.alternative} />
              <Hide onMobile onTablet>
                <SellerCard />
              </Hide>
            </VStack>
          </Stack>
        </Box>
      </Center>
    </>
  );
}
